import { mapGetters } from "vuex";
import NumberValue, {
    NUMBER_VALUE_RANGE,
    NUMBER_VALUE_REQUIRED,
    validateNumberValue,
} from "@/entities/structures/numberValue";
import { ORDERS_ORDER_TYPE, PAYDAY, PAYHOUR } from "@/constants/workloadTypes";

const ORDER_WORKLOAD_TYPES = [ORDERS_ORDER_TYPE, "proceeds_order_type"];
const CATEGORIES_WORKLOAD_TYPES = ["proceeds_category", "products_office"];
const BLOCKED_WORKLOAD_TYPES = ["orders_employee", "orders_office"];
const PLACES_WORKLOAD_TYPES = ["proceeds_place", "products_place"];

const UNIT_NAMES = {
    1: "штук",
    2: "฿",
    3: "часов",
};

const ERRORS = {
    required: "Обязательное поле",
    range: "Неверный диапазон",
};

export default {
    data: {
        workloadDictionaries: {
            workloadTypes: [],
            orderTypes: [],
            categories: [],
            unitTypes: [],
            places: [],
        },
        workloadForm: {
            workloadType: "",
            orderTypes: [],
            categories: [],
            places: [],
            categoriesExcept: false,
            unitType: null,

            productionStandard: new NumberValue(),
        },
        workloadErrors: {
            workloadType: "",
            unitType: "",
            productionStandard: "",
        },
        requiredProductionStandard: true,
    },

    computed: {
        ...mapGetters("positions", ["getWorkloadTypes", "getUnits", "getCategories", "getOrderTypes", "getPlaces"]),

        workloadType() {
            if (!this.workloadForm.workloadType) return null;
            return this.workloadForm.workloadType.value;
        },

        workloadAdditionalParam() {
            if (!this.workloadType) return null;
            if (ORDER_WORKLOAD_TYPES.includes(this.workloadType)) return "orders";
            if (CATEGORIES_WORKLOAD_TYPES.includes(this.workloadType)) return "categories";
            if (PLACES_WORKLOAD_TYPES.includes(this.workloadType)) return "places";

            return null;
        },

        workloadBlockUnits() {
            const meta = this.getWorkloadTypeMeta();

            return (
                (this.workloadType && BLOCKED_WORKLOAD_TYPES.includes(this.workloadType)) ||
                (meta && meta.unitId && !meta.canChangeInPostSettings)
            );
        },

        workloadUnitName() {
            const unitType = this.workloadForm.unitType && this.workloadForm.unitType.value;
            return UNIT_NAMES[unitType] || "";
        },

        isWorkloadValid() {
            return Object.values(this.workloadErrors).every((error) => !error);
        },
    },

    watch: {
        "getWorkloadTypes": function (value) {
            this.workloadDictionaries.workloadTypes = value.map((item) => ({
                label: item.name,
                value: item.code,
                unitId: item.unit_id,
                canChangeInPostSettings: item.can_change_in_post_settings,
                quantitativeIndicator: item.quantitative_indicator,
            }));
        },

        "getUnits": function (value) {
            const items = value.map((item) => ({ label: item.name, value: item.id }));
            this.workloadDictionaries.unitTypes = items;
            const [defaultUnit] = items;
            if (defaultUnit !== undefined) {
                this.workloadForm.unitType = defaultUnit;
            }
        },

        "getCategories": function (value) {
            this.workloadDictionaries.categories = value.map((item) => ({ label: item.name, value: item.id }));
        },

        "getOrderTypes": function (value) {
            this.workloadDictionaries.orderTypes = Object.entries(value).map(([value, label]) => ({
                label,
                value,
            }));
        },

        "getPlaces": function (value) {
            this.workloadDictionaries.places = value.map((item) => ({
                label: item.name,
                value: item.id,
            }));
        },

        "workloadForm.productionStandard": function () {
            this.checkProductionStandard();
        },

        "workloadForm.workloadType": function (value) {
            this.checkWorkloadType();
            const meta =
                value && value.value
                    ? this.workloadDictionaries.workloadTypes.find((item) => item.value === value.value) || value
                    : null;

            if (meta) {
                const unitId = meta.unitId;
                if (unitId !== undefined && unitId !== null) {
                    const unitFromDictionary = this.workloadDictionaries.unitTypes.find((item) => item.value === unitId);
                    this.workloadForm.unitType =
                        unitFromDictionary !== undefined ? unitFromDictionary : { value: unitId };
                }
                this.resetWorkloadTypeParams();
                this.requiredProductionStandard = ![PAYDAY, PAYHOUR].includes(meta.value);
            } else {
                const [defaultUnit] = this.workloadDictionaries.unitTypes;
                this.workloadForm.unitType = defaultUnit !== undefined ? defaultUnit : null;
                this.requiredProductionStandard = true;
            }

            this.checkUnitType();
            this.onWorkloadTypeChange(meta && meta.value);
        },
    },

    created() {
        this.$store.dispatch("positions/getProductionGroups");
        this.$store.dispatch("positions/getWorkloadTypes");
        this.$store.dispatch("positions/getUnits");
        this.$store.dispatch("positions/getCategories");
        this.$store.dispatch("positions/getOrderTypes");
        this.$store.dispatch("positions/getPlaces");

        this.checkWorkloadType();
        this.checkUnitType();
        this.checkProductionStandard();
    },

    methods: {
        onWorkloadTypeChange() {},

        getWorkloadTypeMeta() {
            const selected = this.workloadForm.workloadType;
            if (!selected || !selected.value) {
                return null;
            }

            return this.workloadDictionaries.workloadTypes.find((item) => item.value === selected.value) || selected;
        },

        resolveUnitId() {
            const {unitType} = this.workloadForm;
            if (unitType !== null && unitType !== undefined && unitType.value !== undefined && unitType.value !== null) {
                return unitType.value;
            }

            const meta = this.getWorkloadTypeMeta();
            if (meta !== null && meta.unitId !== undefined && meta.unitId !== null) {
                return meta.unitId;
            }

            return null;
        },

        setWorkload(data) {
            this.workloadForm.workloadType = {
                value: data.workloadType,
            };

            this.$nextTick(() => {
                this.workloadForm.unitType = { value: data.unitType };
                this.workloadForm.categories = data.categories ? data.categories.map((cat) => ({ value: cat })) : [];
                this.workloadForm.categoriesExcept = data.categoriesExcept ? data.categoriesExcept : false;
                this.workloadForm.orderTypes = data.orderTypes ? data.orderTypes.map((type) => ({ value: type })) : [];
                this.workloadForm.places = data.places ? data.places.map((type) => ({ value: type })) : [];
            });

            this.workloadForm.productionStandard = new NumberValue(data.productionStandard);
        },

        resetWorkload() {
            this.workloadForm.workloadType = null;
            const [defaultUnit] = this.workloadDictionaries.unitTypes;
            this.workloadForm.unitType = defaultUnit !== undefined ? defaultUnit : null;
            this.resetWorkloadTypeParams();
            this.resetWorkloadProductionStandard();
        },

        resetWorkloadTypeParams() {
            this.workloadForm.orderTypes = [];
            this.workloadForm.categories = [];
            this.workloadForm.categoriesExcept = false;
        },

        resetWorkloadProductionStandard() {
            this.workloadForm.productionStandard = new NumberValue();
        },

        getWorkloadParams() {
            const { productionStandard, orderTypes, categories, categoriesExcept, places } = this.workloadForm;
            const meta = this.getWorkloadTypeMeta();
            const unitId = this.resolveUnitId();

            if (!meta || !meta.value || unitId === null) {
                return null;
            }

            return {
                workloadType: meta.value,
                unitType: unitId,
                productionStandard,
                orderTypes: orderTypes.map((t) => t.value),
                categories: categories.map((t) => t.value),
                places: places.map((t) => t.value),
                categoriesExcept,
            };
        },

        checkWorkloadType() {
            this.workloadErrors.workloadType = this.workloadForm.workloadType ? "" : ERRORS.required;
        },

        checkUnitType() {
            const unitId = this.resolveUnitId();
            this.workloadErrors.unitType = unitId !== null && unitId !== "" ? "" : ERRORS.required;
        },

        checkProductionStandard() {
            let displayError = "";

            if (this.requiredProductionStandard) {
                const { productionStandard } = this.workloadForm;
                const { error } = validateNumberValue(productionStandard);

                if (error === NUMBER_VALUE_REQUIRED) {
                    displayError = ERRORS.required;
                } else if (error === NUMBER_VALUE_RANGE) {
                    displayError = ERRORS.range;
                }
            }

            this.workloadErrors.productionStandard = displayError;
        },
    },
};
